<script setup lang="ts">
import { computed, ref, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import useStrapiStore from '@/composition/strapiStore'
import useAppStore from '@/composition/appStore'
import type { LikeableResource, Page } from '@/models'
import ResourceCard from '@/components/resources/ResourceCard.vue'
import MultiSelectDropdown from '@/components/MultiSelectDropdown.vue'
import BlockHeader from '@/components/blockStyles/fields/BlockHeader.vue'
import SiteFooter from '@/components/SiteFooter.vue'

const strapiStore = useStrapiStore()
const appStore = useAppStore()
const { t } = useI18n({})

defineProps({
  page: {
    type: Object as PropType<Page>,
    required: false,
  },
})

strapiStore.actions.loadResources()

const classifications = [
  'learning design',
  'theory',
  'method',
  'tool',
  'content',
  'high level topic',
]
const subCategories = [
  { value: 'article', label: 'Article' },
  { value: 'video', label: 'Video' },
  { value: 'tool', label: 'Tool' },
  { value: 'e-book', label: 'Label' },
  { value: 'guideline', label: 'Guideline' },
  { value: 'other', label: 'Other' },
]

const user = appStore.getters.user
const resources = computed(() => Array.from(strapiStore.getters.resources.value.values()))
const selectedClassification = ref('learning design')
const selectedSubCategories = ref<string[]>([])

const filteredLikableResourceList = computed(() => {
  return resources.value
    .filter(
      (r) =>
        r.classification === selectedClassification.value &&
        r.subcategory &&
        (selectedSubCategories.value.includes(r.subcategory) ||
          selectedSubCategories.value.length === 0),
    )
    .map((r) => {
      const likableResource: LikeableResource = {
        ...r,
        liked: user.value?.likedResources.includes(r.documentId) ?? false,
      }
      return likableResource
    })
})

function likeResource(rID: string) {
  appStore.actions.toggleResource('like', rID)
}
</script>

<template>
  <div class="flex flex-col text-black">
    <!-- Search panel -->
    <div
      class="sm:h-[65vh] min-h-128 w-full flex flex-col items-center justify-end bg-green bg-pattern text-white"
    >
      <h1 class="m-4 px-6 mb-4 sm:mb-12 text-xl sm:text-4xl w-4/5 sm:w-3/5">
        {{ t('searchingFor') }}
      </h1>
      <div
        class="mb-16 sm:mb-48 relative border rounded-full bg-[#E4E4E4] text-black w-4/5 sm:w-3/5"
      >
        <input
          class="pl-[56px] w-full p-5 rounded-full font-alexandria font-light text-xl placeholder:text-[#B9B9B9]"
          type="search"
          placeholder="Search resources..."
        />
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          class="absolute left-[24px] top-[50%] translate-y-[-50%] text-[#5C5C5C]"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
      </div>
    </div>

    <!-- content panel -->
    <div class="text-[#123A35] bg-grey/20 font-alexandria">
      <div class="max-w-[1512px] sm:px-[6rem] max-sm:px-[3rem] mx-auto">
        <div class="sm:w-full">
          <BlockHeader class="my-10 text-[#042E2C]" :text="t('discoverHeader')" />
        </div>
        <!-- header -->
        <div
          class="w-full flex flex-col lg:flex-row border-b-3 border-[#042E2CA6] justify-between items-start lg:items-center"
        >
          <div class="flex flex-col max-lg:w-full lg:flex-row items-end">
            <p
              v-for="c in classifications"
              :key="`id-${c}`"
              class="cursor-pointer whitespace-nowrap w-full lg:mr-4 max-lg:px-4 max-lg:py-2 relative after:content-[''] after:absolute after:left-0 after:-bottom-[16px] after:w-full after:h-[3px]"
              :class="{
                'after:bg-black max-lg:after:bg-transparent text-2xl font-semibold max-lg:bg-black max-lg:text-white':
                  selectedClassification === c,
                'text-xl text-[#042E2CA6] font-normal': selectedClassification !== c,
              }"
              @pointerdown="selectedClassification = c"
            >
              {{ t(c) }}
            </p>
          </div>
          <div class="py-2">
            <!--select>
              <option value="all">{{ t('all') }}</option>
              <option v-for="sc in subCategories" :key="`id-${sc}`" :value="sc">{{ t(sc) }}</option>
            </select-->
            <MultiSelectDropdown
              class="w-36 bg-[#E4E4E4]"
              v-model="selectedSubCategories"
              :options="subCategories"
              placeholder="Select categories..."
              :show-search="false"
              :max-display="3"
            />
          </div>
        </div>

        <div>
          <p class="whitespace-pre-line mt-8 font-light text-2xl">
            {{ t(`discoverDescription-${selectedClassification}`) }}
          </p>
        </div>

        <!--  Visible resources -->
        <div
          class="grid grid-cols-1 max-w-max sm:grid-cols-2 md:grid-cols-3 gap-5 mt-20 max-sm:mx-auto"
        >
          <ResourceCard
            v-for="r in filteredLikableResourceList"
            :key="r.documentId"
            :category="r.category"
            :resource="r"
            :liked="r.liked"
            :like-mode="user ? 'like' : 'none'"
            :mode="'regular'"
            @like="likeResource"
          />
        </div>
      </div>
      <SiteFooter class="mt-32" />
    </div>
  </div>
</template>

<style scoped>
.bg-pattern {
  background-image: url('@/assets/pattern-fav.png');
  background-size: 85% 85%;
}
</style>
